import { Artefact, ArtefactType, ArtefactDetectionConfig } from "@/types/artefacts";

import { BaseArtefactDetector } from "./BaseDetector";

/**
 * Generate a human-readable title from column headers
 * Creates concise descriptions like "Company Details", "Name, Age, Location"
 */
function generateTitleFromHeaders(headers: string[]): string {
  if (!headers || headers.length === 0) {
    return "Table";
  }

  // Clean headers - remove markdown links and extra whitespace
  const cleanHeaders = headers.map((h) =>
    h
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // Strip markdown links
      .replace(/\*+/g, "") // Strip bold/italic markers
      .trim(),
  );

  // If 1-3 columns, show all header names
  if (cleanHeaders.length <= 3) {
    return cleanHeaders.filter((h) => h.length > 0).join(", ");
  }

  // For more columns, show first 2 and indicate more
  const firstTwo = cleanHeaders.slice(0, 2).filter((h) => h.length > 0);

  return `${firstTwo.join(", ")} + ${cleanHeaders.length - 2} more`;
}

/**
 * Extract headers from different table formats
 */
function extractHeaders(content: string, format: "json" | "tsv" | "markdown"): string[] {
  try {
    if (format === "json") {
      const parsed = JSON.parse(content);

      if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === "object") {
        return Object.keys(parsed[0]);
      }
    } else if (format === "tsv") {
      const firstLine = content.split("\n")[0];

      return firstLine.split("\t").map((h) => h.trim());
    } else if (format === "markdown") {
      const firstLine = content.split("\n")[0];

      return firstLine
        .split("|")
        .map((h) => h.trim())
        .filter((h) => h.length > 0);
    }
  } catch {
    // Fallback on parse error
  }

  return [];
}

/**
 * Detects data tables (JSON arrays, CSV-like content)
 */
export class TableDetector extends BaseArtefactDetector {
  detect(content: string, messageId: string, _config: ArtefactDetectionConfig) {
    if (!this.validateContent(content)) {
      return this.createResult([], 0, ["Invalid content provided for table detection"]);
    }

    const artefacts: Artefact[] = [];
    const warnings: string[] = [];
    let skippedCount = 0;
    let tableIndex = 0;

    // Detect JSON arrays that look like tables
    const jsonArrayRegex = /\[\s*\{[\s\S]{1,5000}?\}\s*(?:,\s*\{[\s\S]{1,5000}?\}\s*)*\]/g;
    let match;
    let foundJsonArrays = false;

    while ((match = jsonArrayRegex.exec(content)) !== null) {
      foundJsonArrays = true;
      try {
        const jsonContent = match[0];
        const parsed = JSON.parse(jsonContent);

        if (
          Array.isArray(parsed) &&
          parsed.length > 1 &&
          parsed.every((item) => typeof item === "object" && item !== null)
        ) {
          const headers = extractHeaders(jsonContent, "json");
          const title = generateTitleFromHeaders(headers);

          const artefact: Artefact = {
            id: `${messageId}-table-${tableIndex}`,
            type: ArtefactType.TABLE,
            title,
            content: JSON.stringify(parsed, null, 2),
            messageId,
            timestamp: this.getCurrentTimestamp(),
            metadata: {
              fileExtension: "json",
              detectionPattern: "json_array",
              estimatedSize: `${parsed.length} rows`,
            },
          };

          artefacts.push(artefact);
          tableIndex++;
        }
      } catch {
        // Invalid JSON, skip
        skippedCount++;
      }
    }

    // Detect tab-separated tables (TSV format)
    // Must have at least 2 columns and 2 rows (header + 1 data row)
    const tsvRegex =
      /^[^\t\n]+\t[^\t\n]+(?:\t[^\t\n]+)*\n(?:[^\t\n]+\t[^\t\n]+(?:\t[^\t\n]+)*\n?)+/gm;
    let foundTsvTables = false;

    while ((match = tsvRegex.exec(content)) !== null) {
      const tsvContent = match[0].trim();
      const rows = tsvContent.split("\n").filter((row) => row.trim().length > 0);

      // Require at least header + 1 data row
      if (rows.length >= 2) {
        // Check if all rows have the same number of columns (consistency check)
        const columnCounts = rows.map((row) => row.split("\t").length);
        const firstColumnCount = columnCounts[0];
        const isConsistent = columnCounts.every((count) => count === firstColumnCount);

        if (isConsistent && firstColumnCount >= 2) {
          foundTsvTables = true;
          const dataRowCount = rows.length - 1;
          const headers = extractHeaders(tsvContent, "tsv");
          const title = generateTitleFromHeaders(headers);

          const artefact: Artefact = {
            id: `${messageId}-table-${tableIndex}`,
            type: ArtefactType.TABLE,
            title,
            content: tsvContent,
            messageId,
            timestamp: this.getCurrentTimestamp(),
            metadata: {
              fileExtension: "tsv",
              detectionPattern: "tsv_table",
              estimatedSize: `${dataRowCount} ${dataRowCount === 1 ? "row" : "rows"}`,
            },
          };

          artefacts.push(artefact);
          tableIndex++;
        }
      }
    }

    // Detect pipe-delimited markdown tables
    // Improved regex to handle any number of columns (minimum 2)
    const csvRegex =
      /^[^|\n]*\|[^|\n]+(?:\|[^|\n]*)+\n\s*\|?[-:\s]+\|[-|:\s]+(?:\|[-|:\s]+)*\|?\s*\n(?:[^|\n]*\|[^|\n]+(?:\|[^|\n]*)+\n?)+/gm;
    let foundTables = false;

    while ((match = csvRegex.exec(content)) !== null) {
      foundTables = true;
      const csvContent = match[0].trim();
      const rows = csvContent.split("\n").filter((row) => !row.match(/^\s*\|?[-|:\s]+\|?\s*$/));

      // Require at least header + 1 data row (rows.length >= 2)
      if (rows.length >= 2) {
        // Calculate data rows (total rows minus header)
        const dataRowCount = rows.length - 1;
        const headers = extractHeaders(csvContent, "markdown");
        const title = generateTitleFromHeaders(headers);

        const artefact: Artefact = {
          id: `${messageId}-table-${tableIndex}`,
          type: ArtefactType.TABLE,
          title,
          content: csvContent,
          messageId,
          timestamp: this.getCurrentTimestamp(),
          metadata: {
            fileExtension: "csv",
            detectionPattern: "markdown_table",
            estimatedSize: `${dataRowCount} ${dataRowCount === 1 ? "row" : "rows"}`,
          },
        };

        artefacts.push(artefact);
        tableIndex++;
      } else {
        // Skip tables with insufficient data
        skippedCount++;
      }
    }

    // Only count skips if we actually found something to process
    const finalSkipCount = foundJsonArrays || foundTables || foundTsvTables ? skippedCount : 0;

    return this.createResult(artefacts, finalSkipCount, warnings);
  }
}
